import { useMemo, memo, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { Pill, TextInput } from '@mantine/core';
import { DataTable, DataTableProps } from 'mantine-datatable';
import { modals } from '@mantine/modals';
// components
import type { ITopicsParams, ITopicListItem, ITopic } from '@newo/api/gateway';
import DateTime from '@/modules/DateTime/components/DateTime';
import Score from '@/pages/AKB/components/Score';
import Actions from '@/pages/AKB/components/AKBDataTable/components/Actions';
import TopicForm from '@/pages/AKB/components/TopicForm';
// utils
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
import { useDeleteTopicMutation } from '@/services/akb/akb.queries.ts';
import useAKBStore from '@/pages/AKB/AKB.store.ts';
// assets
import styles from '@/pages/AKB/components/AKBDataTable/AKBDataTable.module.css';

interface AKBDataTablePops {
    personaId: string;
    items: ITopicListItem[];
    params: ITopicsParams;
    onParamsChange: (params: ITopicsParams) => void;
    selectedTopics: ITopicListItem[];
    setSelectedTopic: (topics: ITopicListItem[]) => void;
    refetch: () => void;
    isFetching?: boolean;
    className?: string;
}

const handleCloseAll = () => {
    modals.closeAll();
};

const AKBDataTable = ({
    personaId,
    items,
    params,
    onParamsChange,
    selectedTopics,
    setSelectedTopic,
    refetch,
    className,
    isFetching = false
}: AKBDataTablePops) => {
    const { sortStatus, setSortStatus } = useAKBStore(
        useShallow((state) => ({
            sortStatus: state.sortStatus,
            setSortStatus: state.setSortStatus
        }))
    );
    const deleteMutation = useDeleteTopicMutation();

    const handleDelete = useCallback(
        (topicId: string) => {
            openConfirmDeleteModal({
                title: 'Delete Topic?',
                children: 'Are you sure you want to delete topic?',
                confirmLabel: 'Delete topic',
                onConfirm: () => {
                    deleteMutation.mutate(
                        { topicId },
                        {
                            onSuccess() {
                                refetch();
                            }
                        }
                    );
                }
            });
        },
        [deleteMutation, refetch]
    );

    const handleEdit = useCallback(
        (topic: ITopic) => {
            modals.open({
                title: 'Edit Topic',
                size: '70%',
                children: (
                    <TopicForm
                        personaId={personaId}
                        topic={topic}
                        onClose={handleCloseAll}
                    />
                )
            });
        },
        [personaId]
    );

    const handleCopy = useCallback(
        (topic: ITopic) => {
            modals.open({
                title: 'Duplicate Topic',
                size: '70%',
                children: (
                    <TopicForm
                        isCopy
                        personaId={personaId}
                        topic={topic}
                        onClose={handleCloseAll}
                    />
                )
            });
        },
        [personaId]
    );

    const columns: DataTableProps<ITopicListItem>['columns'] = useMemo(
        () => [
            {
                accessor: 'topic_name',
                title: 'Name',
                width: 250,
                filter: (
                    <TextInput
                        label="Filter by topic name"
                        placeholder="Start typing..."
                        value={params.topic_name}
                        onChange={(event) => {
                            onParamsChange({
                                topic_name: event.currentTarget.value
                            });
                        }}
                    />
                ),
                filtering: !!params.topic_name,
                sortable: true,
                render: ({ topic, ...rest }) => (
                    <>
                        <Score score={rest.fuzzy_search_fields_score?.name} />
                        <div className="break-all">{topic.topic_name}</div>
                    </>
                )
            },
            {
                accessor: 'topic_summary',
                title: 'Summary',
                width: '33%',
                filter: (
                    <TextInput
                        label="Filter by topic summary"
                        placeholder="Start typing..."
                        value={params.topic_summary}
                        onChange={(event) => {
                            onParamsChange({
                                topic_summary: event.currentTarget.value
                            });
                        }}
                    />
                ),
                filtering: !!params.topic_summary,
                sortable: true,
                render: ({ topic, ...rest }) => (
                    <>
                        <Score
                            score={rest.fuzzy_search_fields_score?.summary}
                        />
                        <div className="break-all">{topic.topic_summary}</div>
                    </>
                )
            },
            {
                accessor: 'topic_facts',
                title: 'Facts',
                width: '20%',
                filter: (
                    <TextInput
                        label="Filter by topic facts"
                        placeholder="Start typing..."
                        value={params.topic_facts}
                        onChange={(event) => {
                            onParamsChange({
                                topic_facts: event.currentTarget.value
                            });
                        }}
                    />
                ),
                filtering: !!params.topic_facts,
                sortable: true,
                render: ({ topic, ...rest }) => (
                    <>
                        <Score score={rest.fuzzy_search_fields_score?.facts} />
                        <div className="break-all">
                            {topic.topic_facts.join('\n')}
                        </div>
                    </>
                )
            },
            {
                accessor: 'source',
                title: 'Source',
                filter: (
                    <TextInput
                        label="Filter by source"
                        placeholder="Start typing..."
                        value={params.topic_source}
                        onChange={(event) => {
                            onParamsChange({
                                topic_source: event.currentTarget.value
                            });
                        }}
                    />
                ),
                filtering: !!params.topic_source,
                width: 150,
                sortable: true,
                render: ({ topic, ...rest }) => (
                    <>
                        <Score score={rest.fuzzy_search_fields_score?.source} />
                        <div className="break-all">{topic.source}</div>
                    </>
                )
            },
            {
                accessor: 'created_at',
                title: 'Created at',
                width: 140,
                filter: (
                    <TextInput
                        label="Filter by create at"
                        placeholder="Start typing..."
                        value={params.topic_created_at}
                        onChange={(event) => {
                            onParamsChange({
                                topic_created_at: event.currentTarget.value
                            });
                        }}
                    />
                ),
                render: ({ topic }) => (
                    <span className="text-xs">
                        <DateTime value={topic.created_at} />
                    </span>
                ),
                filtering: !!params.topic_created_at,
                sortable: true
            },
            {
                accessor: 'updated_at',
                title: 'Updated at',
                width: 140,
                filter: (
                    <TextInput
                        label="Filter by updated at"
                        placeholder="Start typing..."
                        value={params.topic_updated_at}
                        onChange={(event) => {
                            onParamsChange({
                                topic_updated_at: event.currentTarget.value
                            });
                        }}
                    />
                ),
                render: ({ topic }) => (
                    <span className="text-xs">
                        <DateTime value={topic.updated_at} />
                    </span>
                ),
                filtering: !!params.topic_updated_at,
                sortable: true
            },
            {
                accessor: 'labels',
                title: 'Labels',
                width: 200,
                render: ({ topic }) => {
                    return (
                        <div className="flex flex-wrap gap-1">
                            {topic.labels.map((label) => (
                                <Pill
                                    className={styles.pill}
                                    key={label}
                                    size="sm"
                                >
                                    {label}
                                </Pill>
                            ))}
                        </div>
                    );
                },
                filter: (
                    <TextInput
                        label="Filter by labels"
                        placeholder="Start typing..."
                        value={params.topic_labels}
                        onChange={(event) => {
                            onParamsChange({
                                topic_labels: event.currentTarget.value
                            });
                        }}
                    />
                ),
                filtering: !!params.topic_labels
            },
            {
                accessor: 'actions',
                title: 'actions',
                width: 100,
                render: ({ topic }) => (
                    <Actions
                        topic={topic}
                        onCopy={handleCopy}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                    />
                )
            }
        ],
        [params, handleDelete, handleEdit, handleCopy, onParamsChange]
    );

    return (
        <DataTable
            withTableBorder
            className={className}
            classNames={{
                header: styles.header
            }}
            columns={columns}
            emptyState={<div className="py-10">No records</div>}
            fetching={isFetching}
            idAccessor="topic.topic_id"
            loaderBackgroundBlur={5}
            loaderType="dots"
            minHeight={400}
            miw={1600}
            records={items}
            rowClassName={styles.row}
            scrollAreaProps={{ type: 'always', scrollbarSize: 10 }}
            selectedRecords={selectedTopics}
            sortStatus={sortStatus}
            verticalAlign="top"
            onSelectedRecordsChange={setSelectedTopic}
            onSortStatusChange={setSortStatus}
        />
    );
};

export default memo(AKBDataTable);
